import React, { useState, useEffect, useMemo } from 'react';
import { X, Sparkles, Mail, Volume2, ExternalLink, Wand2, Shirt, Info, Headphones, Eye, AlertTriangle, Globe, CalendarClock } from 'lucide-react';
import { agentsAPI, templatesAPI, templateArticleCount, PromptTemplate } from '../services/api';
import api from '../services/api';
import { AgentConfig, HandoffWeekday, HumanHandoffSchedule } from '../types';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { slugifyTyping, slugifyStrict } from '../utils/slug';
import ImageUploadWithCrop from './ImageUploadWithCrop';
import { useToast } from '../contexts/ToastContext';
import { useBilling } from '../contexts/BillingContext';
import { useBillingMode } from '../hooks/useBillingMode';
import WidgetPreview from './WidgetPreview';
import { playPcmAudio } from '../utils/playPcmAudio';
import { AGENT_EMAIL_DOMAIN, APP_CONFIG } from '../config/appConfig';

const IS_SHOPIFY_BUILD = APP_CONFIG.billingProvider === 'shopify';

// Plan-TalkToHumanToggleSchedule.md — Talk-to-Human availability schedule.
// Per-weekday on/off + ONE time range each (v1, §9-B).
const HANDOFF_WEEKDAYS: { key: HandoffWeekday; label: string }[] = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

// Curated IANA timezone list for the schedule picker (§9-C) — the common
// zones, not all ~400. The saved value is the raw IANA string; all
// availability math is done server-side against it.
const HANDOFF_TIMEZONES: string[] = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Moscow',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
];

type HandoffDayState = { enabled: boolean; start: string; end: string };

// Default editor day-map: Mon–Fri 09:00–17:00 on, weekend off. Used when a
// merchant first switches on "Set hours" (no saved schedule yet).
function defaultHandoffDays(): Record<HandoffWeekday, HandoffDayState> {
  return {
    mon: { enabled: true, start: '09:00', end: '17:00' },
    tue: { enabled: true, start: '09:00', end: '17:00' },
    wed: { enabled: true, start: '09:00', end: '17:00' },
    thu: { enabled: true, start: '09:00', end: '17:00' },
    fri: { enabled: true, start: '09:00', end: '17:00' },
    sat: { enabled: false, start: '09:00', end: '17:00' },
    sun: { enabled: false, start: '09:00', end: '17:00' },
  };
}

// Hydrate the editor day-map from a saved schedule: a present day → enabled
// with its range; a missing day → closed (disabled) with a sensible default.
function handoffDaysFromSchedule(
  schedule: HumanHandoffSchedule | null | undefined,
): Record<HandoffWeekday, HandoffDayState> {
  const base = defaultHandoffDays();
  (Object.keys(base) as HandoffWeekday[]).forEach((k) => {
    const range = schedule?.days?.[k];
    base[k] = range
      ? { enabled: true, start: range.start, end: range.end }
      : { ...base[k], enabled: false };
  });
  return base;
}

interface AgentEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  initialConfig?: AgentConfig;
  agentId?: string;
  agentEmail?: string;
  isEdit?: boolean;
  s3Assets?: Record<string, string>;
  /** Stable public Help Center slug (top-level on the agent, NOT in config).
   *  Edit-mode only — the server generates it at creation. */
  helpCenterSlug?: string;
}

const AgentEditorModal: React.FC<AgentEditorModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  initialConfig,
  agentId,
  agentEmail,
  isEdit = false,
  s3Assets,
  helpCenterSlug,
}) => {
  const { showToast } = useToast();
  const { activeWorkspace } = useWorkspace();
  const { billing } = useBilling();
  // `=== 'legacy'`, never `!== 'pooled'` — while billing loads the mode is
  // unknown, and unknown is not legacy. See the hook.
  const showLegacyCredits = useBillingMode() === 'legacy';
  const hasLiveAvatarAddon = billing?.liveavatar_addon?.status === 'active';
  // Shopify Managed-Pricing bundles LiveAvatar into the Business plan, so
  // gate the toggle on plan-or-addon for that build; the Stripe build keeps
  // the standalone addon gate.
  const canUseAvatar = IS_SHOPIFY_BUILD
    ? (billing?.plan === 'business' || hasLiveAvatarAddon)
    : hasLiveAvatarAddon;
  const avatarUnavailableCopy = IS_SHOPIFY_BUILD
    ? 'Available with the Business plan'
    : 'Available with AI Avatar addon — upgrade in Billing';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [agentName, setAgentName] = useState('Support Assistant');
  const [companyName, setCompanyName] = useState('');
  const [logoLight, setLogoLight] = useState<string>('');
  const [logoDark, setLogoDark] = useState<string>('');
  const [brandColor, setBrandColor] = useState('#1e73be');
  const [brandColorDark, setBrandColorDark] = useState('#1e73be');
  const [welcomeMessage, setWelcomeMessage] = useState('Hello! How can I help you today?');
  const [tone, setTone] = useState<'professional' | 'friendly' | 'casual' | 'formal'>('friendly');
  const [industry, setIndustry] = useState('');
  const [templates, setTemplates] = useState<any[]>([]);
  // Prodagi Templates — a DIFFERENT collection from `templates` above, which is
  // `industry_templates`: a {id,title,instruction} preset whose `instruction`
  // the editor no longer reads (Custom Instructions retired 2026-08-24), so
  // only its id/title feed the industry picker. These carry a knowledge-article
  // pack and are joined to the industry preset by `industry_key`.
  const [packTemplates, setPackTemplates] = useState<PromptTemplate[]>([]);
  const [useTemplatePack, setUseTemplatePack] = useState(false);

  // Launcher configuration
  const [launcherType, setLauncherType] = useState<'default' | 'custom'>('default');
  const [launcherIconId, setLauncherIconId] = useState<string>('message');
  const [launcherIconLight, setLauncherIconLight] = useState<string>('');
  const [launcherIconDark, setLauncherIconDark] = useState<string>('');

  // S3 Upload Blobs (Pending uploads)
  const [logoLightBlob, setLogoLightBlob] = useState<Blob | null>(null);
  const [logoDarkBlob, setLogoDarkBlob] = useState<Blob | null>(null);
  const [launcherIconLightBlob, setLauncherIconLightBlob] = useState<Blob | null>(null);
  const [launcherIconDarkBlob, setLauncherIconDarkBlob] = useState<Blob | null>(null);

  // New Customization States
  const [showAiIcon, setShowAiIcon] = useState(true);
  const [aiIconPosition, setAiIconPosition] = useState<'left' | 'right'>('left');
  const [showAiLabel, setShowAiLabel] = useState(true);
  const [aiLabelText, setAiLabelText] = useState('AI');
  const [showCustomerIcon, setShowCustomerIcon] = useState(true);
  const [customerIconPosition, setCustomerIconPosition] = useState<'left' | 'right'>('left');
  const [showCustomerLabel, setShowCustomerLabel] = useState(true);
  const [customerLabelText, setCustomerLabelText] = useState('You');
  const [customerFontColor, setCustomerFontColor] = useState('#ffffff');
  const [customerIconUrl, setCustomerIconUrl] = useState<string>('');
  const [customerIconBlob, setCustomerIconBlob] = useState<Blob | null>(null);

  const [headerLogoShape, setHeaderLogoShape] = useState<'square' | 'rectangle'>('square');
  const [showHeaderTitles, setShowHeaderTitles] = useState(true);
  const [showAgentName, setShowAgentName] = useState(true);
  // A stored logo was cropped at the aspect ratio of the shape that was in
  // force when it was uploaded. Changing the shape does NOT re-crop it — the
  // cropper only runs on a new pick — so a 1:1 logo stays 1:1 under
  // "rectangle" and the setting looks like it did nothing. Flag the stored
  // logos when the shape changes so each uploader can offer a re-crop.
  const [logoLightNeedsRecrop, setLogoLightNeedsRecrop] = useState(false);
  const [logoDarkNeedsRecrop, setLogoDarkNeedsRecrop] = useState(false);
  const [aiIconLight, setAiIconLight] = useState<string>('');
  const [aiIconDark, setAiIconDark] = useState<string>('');
  const [aiIconLightBlob, setAiIconLightBlob] = useState<Blob | null>(null);
  const [aiIconDarkBlob, setAiIconDarkBlob] = useState<Blob | null>(null);

  // Email Channel
  const [autoRespondEmails, setAutoRespondEmails] = useState(true);
  const [askForCsat, setAskForCsat] = useState(true);
  const [enableChatHistory, setEnableChatHistory] = useState(true);
  const [followUpEnabled, setFollowUpEnabled] = useState(initialConfig?.follow_up_enabled ?? false);
  const [followUpIntervalMinutes, setFollowUpIntervalMinutes] = useState(initialConfig?.follow_up_interval_minutes ?? 5);
  const [followUpMaxCount, setFollowUpMaxCount] = useState(initialConfig?.follow_up_max_count ?? 3);
  // Talk-to-Human toggle + availability schedule (Plan-TalkToHumanToggleSchedule.md).
  // Back-compat: default ENABLED so existing agents keep the button.
  // Public Help Center slug. Frozen by default: seeded from the SAVED value and
  // never touched by the agent-name field, which is the entire point of the fix.
  const [hcSlug, setHcSlug] = useState(helpCenterSlug ?? '');
  const [hcSlugError, setHcSlugError] = useState<string | null>(null);
  // What we would actually SAVE, and therefore what the preview must show —
  // `hcSlug` may hold a mid-typing value like "spago-" that never gets sent.
  const hcSlugStrict = slugifyStrict(hcSlug);
  // An emptied field is NOT a slug change: the server 400s on empty, and
  // "I cleared the box" must not become a save error. Empty ⇒ we send nothing
  // and the saved slug stands.
  const hcSlugChanged = isEdit && !!hcSlugStrict && hcSlugStrict !== (helpCenterSlug ?? '');
  const hcWorkspaceSlug = activeWorkspace?.slug ?? '';
  // The public Help Center is served from the API origin (help.prodagi.ai in
  // prod), NOT the dashboard origin — same base HelpCenterStudio previews from.
  const hcBaseHost = (import.meta.env.VITE_API_URL || window.location.origin)
    .replace(/^https?:\/\//, '')
    .replace(/\/+$/, '');

  const [handoffEnabled, setHandoffEnabled] = useState(initialConfig?.human_handoff_enabled ?? true);
  // Presentation-only: hides the customer CTA without disabling handoff.
  // Default ON — an agent that never set it keeps its button.
  const [handoffButtonVisible, setHandoffButtonVisible] = useState(
    initialConfig?.human_handoff_button_visible ?? true,
  );
  // Presentation-only: hides the off-hours notice (and the hours from the AI's
  // replies). Default ON — an agent that never set it keeps today's notice.
  const [handoffHoursVisible, setHandoffHoursVisible] = useState(
    initialConfig?.human_handoff_hours_visible ?? true,
  );
  // `useSchedule` false = "Available 24/7 when on" (schedule=null); true = "Set hours".
  const [handoffUseSchedule, setHandoffUseSchedule] = useState<boolean>(
    !!initialConfig?.human_handoff_schedule,
  );
  const [handoffTimezone, setHandoffTimezone] = useState<string>(
    initialConfig?.human_handoff_schedule?.timezone || 'UTC',
  );
  const [handoffDays, setHandoffDays] = useState<Record<HandoffWeekday, HandoffDayState>>(
    () => handoffDaysFromSchedule(initialConfig?.human_handoff_schedule),
  );
  // The schedule as it stands in the editor RIGHT NOW, shaped exactly as
  // handleSubmit will persist it, so the live preview reflects unsaved edits
  // (hours, timezone, 24/7-vs-set-hours) without a round-trip.
  const handoffPreviewSchedule: HumanHandoffSchedule | null = useMemo(
    () => (handoffEnabled && handoffUseSchedule
      ? {
          timezone: handoffTimezone,
          days: HANDOFF_WEEKDAYS.reduce((acc, { key }) => {
            const d = handoffDays[key];
            if (d.enabled) acc[key] = { start: d.start, end: d.end };
            return acc;
          }, {} as HumanHandoffSchedule['days']),
        }
      : null),
    [handoffEnabled, handoffUseSchedule, handoffTimezone, handoffDays],
  );
  // Default voice is Aoede (Female). The customer-facing onboarding
  // picker is limited to female voices to match the single deployed
  // HeyGen avatar (operator-configured via backend env). Pre-existing
  // male-voice agents auto-migrate to Aoede on their next save via
  // Storm's server-side backfill.
  const [voiceName, setVoiceName] = useState<string>('Aoede');
  const [avatarEnabled, setAvatarEnabled] = useState<boolean>(false);
  // AI Stylist (Shopify build only) — persists the `stylist_enabled` flag on
  // the agent config. The Stylist engine itself is a separate future build;
  // this only surfaces the per-agent activation toggle.
  const [stylistEnabled, setStylistEnabled] = useState(false);
  // Virtual Try-On (Shopify build only) — persists the `virtual_tryon_enabled`
  // flag on the agent config. The try-on pipeline itself is a later phase;
  // this only surfaces the per-agent activation toggle (Phase 1 shell).
  const [virtualTryonEnabled, setVirtualTryonEnabled] = useState(false);

  const clampInterval = (v: number) => Math.min(10, Math.max(1, Math.round(v)));
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    }
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    // Fetch templates
    const fetchTemplates = async () => {
      try {
        const response = await agentsAPI.getIndustryTemplates();
        setTemplates(response.data.templates);
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      }
    };
    fetchTemplates();

    // Prodagi Templates. Create-only surface, so don't fetch when editing.
    // A failure here just means the toggle never appears — never block the modal.
    if (!isEdit) {
      templatesAPI
        .list()
        .then((res) => setPackTemplates(res.data?.templates ?? []))
        .catch(() => setPackTemplates([]));
    }

    if (isOpen && isEdit && initialConfig) {
      setAgentName(initialConfig.agent_name);
      setCompanyName(initialConfig.company_name);
      setLogoLight(s3Assets?.logo_light || s3Assets?.logo || '');
      setLogoDark(s3Assets?.logo_dark || s3Assets?.logo || '');
      setBrandColor(initialConfig.brand_color);
      setBrandColorDark(initialConfig.brand_color_dark || initialConfig.brand_color || '#1e73be');
      setWelcomeMessage(initialConfig.welcome_message);
      setTone(initialConfig.tone);
      setIndustry(initialConfig.industry || '');

      // Launcher
      setLauncherType(initialConfig.launcher_icon_type || 'default');
      setLauncherIconId(initialConfig.launcher_icon_id || 'message');
      setLauncherIconLight(s3Assets?.launcher_icon_light || s3Assets?.launcher_icon || '');
      setLauncherIconDark(s3Assets?.launcher_icon_dark || s3Assets?.launcher_icon || '');

      setShowAiIcon(initialConfig.show_ai_icon ?? true);
      setAiIconPosition(initialConfig.ai_icon_position ?? 'left');
      setShowAiLabel(initialConfig.show_ai_label ?? true);
      setAiLabelText(initialConfig.ai_label_text ?? 'AI');

      setShowCustomerIcon(initialConfig.show_customer_icon ?? true);
      setCustomerIconPosition(initialConfig.customer_icon_position ?? 'left');
      setShowCustomerLabel(initialConfig.show_customer_label ?? true);
      setCustomerLabelText(initialConfig.customer_label_text ?? 'You');
      setCustomerFontColor(initialConfig.customer_font_color ?? '#ffffff');
      setCustomerIconUrl(s3Assets?.customer_icon || '');

      setHeaderLogoShape(initialConfig.header_logo_shape ?? 'square');
      setShowHeaderTitles(initialConfig.show_header_text ?? true);
      setShowAgentName(initialConfig.show_agent_name ?? true);
      setLogoLightNeedsRecrop(false);
      setLogoDarkNeedsRecrop(false);
      setAiIconLight(s3Assets?.ai_icon_light || s3Assets?.ai_icon || '');
      setAiIconDark(s3Assets?.ai_icon_dark || s3Assets?.ai_icon || '');
      setAutoRespondEmails(initialConfig.auto_respond_emails ?? true);
      setAskForCsat(initialConfig.ask_for_csat ?? true);
      setEnableChatHistory(initialConfig.enable_chat_history ?? true);
      setFollowUpEnabled(initialConfig.follow_up_enabled ?? false);
      setFollowUpIntervalMinutes(initialConfig.follow_up_interval_minutes ?? 5);
      setFollowUpMaxCount(initialConfig.follow_up_max_count ?? 3);
      // Talk-to-Human — default enabled (back-compat); hydrate schedule if set.
      setHandoffEnabled(initialConfig.human_handoff_enabled ?? true);
      setHandoffButtonVisible(initialConfig.human_handoff_button_visible ?? true);
      setHandoffHoursVisible(initialConfig.human_handoff_hours_visible ?? true);
      setHcSlug(helpCenterSlug ?? '');
      setHcSlugError(null);
      setHandoffUseSchedule(!!initialConfig.human_handoff_schedule);
      setHandoffTimezone(initialConfig.human_handoff_schedule?.timezone || 'UTC');
      setHandoffDays(handoffDaysFromSchedule(initialConfig.human_handoff_schedule));
      // If a previously-saved agent has a male voice (Puck/Charon/etc),
      // keep it for visual continuity until Storm's server-side migration
      // flips it to Aoede on next save. The picker below filters to
      // female-only options, so the user will see Aoede selected (the
      // male value won't match any radio) and any save will commit Aoede.
      setVoiceName(initialConfig.voice_name || 'Aoede');
      setAvatarEnabled(initialConfig.avatar_enabled ?? false);
      setStylistEnabled(initialConfig.stylist_enabled ?? false);
      setVirtualTryonEnabled(initialConfig.virtual_tryon_enabled ?? false);
    } else if (isOpen && !isEdit) {
      // Reset to defaults for new agent
      setAgentName('Support Assistant');
      setCompanyName('');
      setLogoLight('');
      setLogoDark('');
      setBrandColor('#1e73be');
      setBrandColorDark('#1e73be');
      setWelcomeMessage('Hello! How can I help you today?');
      setTone('friendly');
      setIndustry('');

      setLauncherType('default');
      setLauncherIconId('message');
      setLauncherIconLight('');
      setLauncherIconDark('');

      setShowAiIcon(true);
      setAiIconPosition('left');
      setShowAiLabel(true);
      setAiLabelText('AI');

      setShowCustomerIcon(true);
      setCustomerIconPosition('left');
      setShowCustomerLabel(true);
      setCustomerLabelText('You');
      setCustomerFontColor('#ffffff');
      setCustomerIconUrl('');

      setHeaderLogoShape('square');
      setShowHeaderTitles(true);
      setShowAgentName(true);
      setLogoLightNeedsRecrop(false);
      setLogoDarkNeedsRecrop(false);
      setAiIconLight('');
      setAiIconDark('');
      setAutoRespondEmails(true);
      setEnableChatHistory(true);
      setFollowUpMaxCount(3);
      // New agent: Talk-to-Human ON by default, no hours restriction.
      setHandoffEnabled(true);
      setHandoffButtonVisible(true);
      setHandoffHoursVisible(true);
      setHcSlug('');
      setHcSlugError(null);
      setHandoffUseSchedule(false);
      setHandoffTimezone('UTC');
      setHandoffDays(defaultHandoffDays());
      setVoiceName('Aoede');
      setAvatarEnabled(false);
      setStylistEnabled(false);
      setVirtualTryonEnabled(false);
    }
    // `helpCenterSlug` is a primitive, so this re-seeds only when the SAVED
    // value actually changes (late agent fetch, or a refetch after save) —
    // never mid-typing.
  }, [isOpen, isEdit, initialConfig, s3Assets, helpCenterSlug]);

  const handleIndustryChange = (selectedIndustry: string) => {
    setIndustry(selectedIndustry);
    // The template-pack toggle belongs to the previously selected industry —
    // clear it so we can't seed a pack the user never saw offered.
    setUseTemplatePack(false);
  };

  // The Prodagi Template (if any) that matches the selected industry. Drives
  // the "Start from a template" toggle — no match means no toggle, never a
  // dead control. Create-only: applying a pack to an agent that already has
  // knowledge is a merge problem out of scope for v1.
  const matchingPackTemplate = useMemo(
    () =>
      isEdit || !industry
        ? undefined
        : packTemplates.find(
            (t) => t.industry_key === industry && t.is_active !== false,
          ),
    [isEdit, industry, packTemplates],
  );

  const handleVoicePreview = async () => {
    setIsPreviewLoading(true);
    setPreviewError(null);
    try {
      const response = await api.get(`/api/voice/preview`, { params: { voice_name: voiceName } });
      const { audio } = response.data;
      // Decode + play via the shared util. 24 kHz default matches the
      // Gemini Live TTS sample rate the backend hands back.
      await playPcmAudio(audio, 24000);
    } catch {
      setPreviewError('Failed to preview voice.');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  if (!isOpen) return null;

  const validateAiDisclaimer = (): boolean => {
    const hasAiAgentInWelcome = welcomeMessage.toLowerCase().includes('ai agent');
    const hasAiInLabel = showAiLabel && aiLabelText.toLowerCase().includes('ai');
    return hasAiAgentInWelcome || hasAiInLabel;
  };

  const handleSaveAgent = async () => {
    if (!validateAiDisclaimer()) {
      showToast("AI disclaimer required: the welcome message must include 'AI Agent', or enable the AI label with text containing 'AI'.", 'error');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Build the Talk-to-Human schedule only when handoff is ON and the
      // merchant chose "Set hours"; otherwise null = always available when on.
      // Only enabled weekdays are emitted (a missing day = closed, server-side).
      const handoffSchedule: HumanHandoffSchedule | null =
        handoffEnabled && handoffUseSchedule
          ? {
              timezone: handoffTimezone,
              days: HANDOFF_WEEKDAYS.reduce((acc, { key }) => {
                const d = handoffDays[key];
                if (d.enabled) acc[key] = { start: d.start, end: d.end };
                return acc;
              }, {} as HumanHandoffSchedule['days']),
            }
          : null;

      const config: AgentConfig = {
        agent_name: agentName,
        company_name: companyName,
        brand_color: brandColor,
        brand_color_dark: brandColorDark,
        welcome_message: welcomeMessage,
        tone: tone,
        enable_voice: true,
        enable_ticket_creation: true,
        enable_google_search: true,
        auto_escalate_threshold: 3,
        widget_position: 'bottom-right',

        launcher_icon_type: launcherType,
        launcher_icon_id: launcherIconId,
        industry: industry,
        show_ai_icon: showAiIcon,
        ai_icon_position: aiIconPosition,
        show_ai_label: showAiLabel,
        ai_label_text: aiLabelText,
        show_customer_icon: showCustomerIcon,
        customer_icon_position: customerIconPosition,
        show_customer_label: showCustomerLabel,
        customer_label_text: customerLabelText,
        customer_font_color: customerFontColor,
        header_logo_shape: headerLogoShape,
        show_header_text: showHeaderTitles,
        show_agent_name: showAgentName,
        auto_respond_emails: autoRespondEmails,
        ask_for_csat: askForCsat,
        enable_chat_history: enableChatHistory,
        follow_up_enabled: followUpEnabled,
        follow_up_interval_minutes: followUpEnabled ? clampInterval(followUpIntervalMinutes) : 5,
        follow_up_max_count: followUpEnabled ? Math.min(3, Math.max(1, followUpMaxCount)) : 3,
        human_handoff_enabled: handoffEnabled,
        human_handoff_button_visible: handoffButtonVisible,
        human_handoff_hours_visible: handoffHoursVisible,
        human_handoff_schedule: handoffSchedule,
        voice_name: voiceName,
        avatar_enabled: avatarEnabled && canUseAvatar,
        stylist_enabled: stylistEnabled,
        virtual_tryon_enabled: virtualTryonEnabled,
      };

      let finalAgentId = agentId;

      if (isEdit && agentId) {
        // Consolidated update: Config + Files in one request
        const filesToUpload: { [key: string]: Blob | null } = {
          logo_light: logoLightBlob,
          logo_dark: logoDarkBlob,
          launcher_icon_light: launcherIconLightBlob,
          launcher_icon_dark: launcherIconDarkBlob,
          ai_icon_light: aiIconLightBlob,
          ai_icon_dark: aiIconDarkBlob,
          customer_icon: customerIconBlob,
        };

        // Send the STRICT form, and only when it actually changed. An unchanged
        // send would pointlessly re-run the server's uniqueness check, and an
        // empty one would 400 (see hcSlugChanged — emptied field sends nothing).
        // 🚨 Top-level field, NOT a key inside `config` (see agentsAPI.update).
        await agentsAPI.update(agentId, {
          config,
          ...(hcSlugChanged ? { help_center_slug: hcSlugStrict } : {}),
          files: filesToUpload
        });
      } else {
        const response = await agentsAPI.create(config);
        finalAgentId = response.data.agent_id;

        // For creation, we still use the separate bulk upload
        if (finalAgentId) {
          const filesToUpload: { [key: string]: Blob | null } = {
            logo_light: logoLightBlob,
            logo_dark: logoDarkBlob,
            launcher_icon_light: launcherIconLightBlob,
            launcher_icon_dark: launcherIconDarkBlob,
            ai_icon_light: aiIconLightBlob,
            ai_icon_dark: aiIconDarkBlob,
            customer_icon: customerIconBlob,
          };

          const hasFiles = Object.values(filesToUpload).some(blob => blob !== null);

          if (hasFiles) {
            await agentsAPI.bulkUploadImages(finalAgentId, filesToUpload);
          }

          // Seed the template's knowledge pack onto the agent we just created.
          // Deliberately non-fatal: the agent exists and is usable, so a failed
          // pack is a warning, not a rolled-back creation. The user can apply
          // the template from the Templates page instead.
          if (useTemplatePack && matchingPackTemplate) {
            try {
              const res = await templatesAPI.apply(
                matchingPackTemplate.template_id,
                finalAgentId,
              );
              showToast(
                `Added ${res.data.articles_created} knowledge articles from "${matchingPackTemplate.title}".`,
                'success',
              );
            } catch {
              showToast(
                "Agent created, but we couldn't add the template's knowledge articles. You can apply the template from the Templates page.",
                'error',
              );
            }
          }
        }
      }

      onComplete();
    } catch (err: any) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : '';
      // The server rejects a bad slug (duplicate / bad format / reserved word)
      // with a 4xx and a human message. If we sent a slug change, that is
      // overwhelmingly the cause — pin the server's own words next to the field
      // so they persist, instead of a toast the user can miss.
      if (hcSlugChanged && status >= 400 && status < 500) {
        setHcSlugError(message || "That URL isn't available. Try a different one.");
      } else {
        showToast(message || `Failed to ${isEdit ? 'update' : 'create'} agent`, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-5xl bg-white dark:bg-gray-800 shadow-2xl flex flex-col h-full overflow-hidden animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center overflow-hidden p-1">
              <img src="/logo-only-white.png" alt="AskProdigy.ai" className="w-full h-full object-contain" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {isEdit ? 'Edit AI Agent' : 'Welcome to AskProdigy AI! 🎉'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isEdit ? `Update configuration for ${agentName}` : "Let's create your first AI agent"}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 lg:overflow-hidden flex flex-col lg:flex-row min-h-0">
          {/* Left Column: Form */}
          <div className="flex-1 flex flex-col min-w-0 min-h-0 border-r border-gray-100 dark:border-gray-700">
            {/* Form Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg">
                  {error}
                </div>
              )}

              {isEdit && !validateAiDisclaimer() && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg flex items-start gap-2">
                  <span className="text-red-500 text-lg leading-none">⚠️</span>
                  <div>
                    <p className="text-sm font-semibold text-red-700 dark:text-red-400">AI Disclosure Requirement Not Met</p>
                    <p className="text-xs text-red-600 dark:text-red-300 mt-0.5">
                      This agent must be updated before saving. Include <strong>"AI Agent"</strong> in the welcome message, or enable <strong>Show AI Text Label</strong> with <strong>"AI"</strong> in the label text.
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Agent Name *
                  </label>
                  <input
                    type="text"
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value)}
                    placeholder="e.g., Support Assistant, Help Bot"
                    className="input-field"
                    required
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    This is how your AI agent will introduce itself
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="e.g., Acme Corp"
                    className="input-field"
                    required
                  />
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-start gap-1">
                    <span>⚠️</span>
                    <span><strong>Important:</strong> Used to generate your agent's email address and chat widget header.</span>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Company Logo
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Upload logos for light and dark themes. These appear in the chat header.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <ImageUploadWithCrop
                      label="Logo (Light Mode)"
                      initialImage={logoLight}
                      aspectRatio={headerLogoShape === 'rectangle' ? 3 : 1}
                      recropHint={logoLightNeedsRecrop}
                      onImageChange={(blob, preview) => {
                        setLogoLightBlob(blob);
                        setLogoLight(preview || '');
                        setLogoLightNeedsRecrop(false);
                      }}
                    />
                    <ImageUploadWithCrop
                      label="Logo (Dark Mode)"
                      initialImage={logoDark}
                      aspectRatio={headerLogoShape === 'rectangle' ? 3 : 1}
                      recropHint={logoDarkNeedsRecrop}
                      onImageChange={(blob, preview) => {
                        setLogoDarkBlob(blob);
                        setLogoDark(preview || '');
                        setLogoDarkNeedsRecrop(false);
                      }}
                    />
                  </div>
                </div>


                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Welcome Message *
                  </label>
                  <textarea
                    value={welcomeMessage}
                    onChange={(e) => setWelcomeMessage(e.target.value)}
                    placeholder="The first message visitors will see"
                    className="input-field h-20"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">Tip: Include "AI Agent" to meet the AI disclosure requirement.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Industry / Use Case
                  </label>
                  <select
                    value={industry}
                    onChange={(e) => handleIndustryChange(e.target.value)}
                    className="input-field"
                  >
                    <option value="">Select an industry...</option>
                    {templates.map((tpl) => (
                      <option key={tpl.id} value={tpl.id}>
                        {tpl.title}
                      </option>
                    ))}
                    <option value="other">Other / General</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    We'll tailor the agent's instructions based on your industry.
                  </p>
                </div>

                {/* Prodagi Template pack — create-only, and only when a template
                    actually exists for the selected industry (no dead toggle).
                    Off by default: the industry preset still overwrites Custom
                    Instructions exactly as before, unchanged. */}
                {matchingPackTemplate && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                    <label className="flex items-start justify-between gap-3 cursor-pointer">
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                          Start from a template
                        </span>
                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5 leading-relaxed">
                          Creates {templateArticleCount(matchingPackTemplate)}{' '}
                          knowledge{' '}
                          {templateArticleCount(matchingPackTemplate) === 1
                            ? 'article'
                            : 'articles'}{' '}
                          from "{matchingPackTemplate.title}" that you can edit
                          or delete in the Knowledge Hub. Your agent settings
                          above are not changed.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setUseTemplatePack(!useTemplatePack)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 mt-0.5 ${useTemplatePack ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${useTemplatePack ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                    </label>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tone of Voice *
                  </label>
                  <select
                    value={tone}
                    onChange={(e) => setTone(e.target.value as any)}
                    className="input-field"
                  >
                    <option value="professional">Professional</option>
                    <option value="friendly">Friendly</option>
                    <option value="casual">Casual</option>
                    <option value="formal">Formal</option>
                  </select>
                </div>
              </div>

              <div className="space-y-6 pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                {/* Branding Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Branding & Appearance</h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Brand Color (Light Mode)
                      </label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="color"
                          value={brandColor}
                          onChange={(e) => setBrandColor(e.target.value)}
                          className="w-12 h-12 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={brandColor}
                          onChange={(e) => setBrandColor(e.target.value)}
                          className="input-field flex-1"
                          placeholder="#1e73be"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Brand Color (Dark Mode)
                      </label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="color"
                          value={brandColorDark}
                          onChange={(e) => setBrandColorDark(e.target.value)}
                          className="w-12 h-12 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={brandColorDark}
                          onChange={(e) => setBrandColorDark(e.target.value)}
                          className="input-field flex-1"
                          placeholder="#1e73be"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-100">
                    <h4 className="text-sm font-medium text-gray-900">Chat Widget Icon</h4>
                    <p className="text-xs text-gray-500 mb-3">Customize the floating button that opens the chat.</p>

                    <div className="flex space-x-4">
                      <button
                        className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${launcherType === 'default' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}
                        onClick={() => setLauncherType('default')}
                      >
                        Default Icons
                      </button>
                      <button
                        className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${launcherType === 'custom' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}
                        onClick={() => setLauncherType('custom')}
                      >
                        Custom Upload
                      </button>
                    </div>

                    {launcherType === 'default' ? (
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { id: 'message', label: 'Message' },
                          { id: 'bot', label: 'Robot' },
                          { id: 'sparkles', label: 'Sparkles' }
                        ].map(icon => (
                          <button
                            key={icon.id}
                            onClick={() => setLauncherIconId(icon.id)}
                            className={`p-4 rounded-xl border-2 flex flex-col items-center space-y-2 transition-all ${launcherIconId === icon.id
                              ? 'border-blue-600 bg-blue-50'
                              : 'border-gray-100 hover:border-gray-200'
                              }`}
                          >
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${launcherIconId === icon.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
                              }`}>
                              {icon.id === 'message' && (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                </svg>
                              )}
                              {icon.id === 'bot' && (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="11" width="18" height="10" rx="2" />
                                  <circle cx="12" cy="5" r="2" />
                                  <path d="M12 7v4" />
                                  <line x1="8" y1="16" x2="8" y2="16" />
                                  <line x1="16" y1="16" x2="16" y2="16" />
                                </svg>
                              )}
                              {icon.id === 'sparkles' && (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                                </svg>
                              )}
                            </div>
                            <span className={`text-xs font-medium ${launcherIconId === icon.id ? 'text-blue-700' : 'text-gray-600'}`}>{icon.label}</span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        <ImageUploadWithCrop
                          label="Widget Icon (Light)"
                          initialImage={launcherIconLight}
                          onImageChange={(blob, preview) => {
                            setLauncherIconLightBlob(blob);
                            setLauncherIconLight(preview || '');
                          }}
                        />
                        <ImageUploadWithCrop
                          label="Widget Icon (Dark)"
                          initialImage={launcherIconDark}
                          onImageChange={(blob, preview) => {
                            setLauncherIconDarkBlob(blob);
                            setLauncherIconDark(preview || '');
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Header & Chat Customization Section */}
                <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Header & Chat Interface</h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Header Logo Shape
                      </label>
                      <select
                        value={headerLogoShape}
                        onChange={(e) => {
                          const next = e.target.value as 'square' | 'rectangle';
                          if (next !== headerLogoShape) {
                            // Only flag logos that actually exist — an empty
                            // slot has nothing to re-crop.
                            setLogoLightNeedsRecrop(!!logoLight);
                            setLogoDarkNeedsRecrop(!!logoDark);
                          }
                          setHeaderLogoShape(next);
                        }}
                        className="input-field"
                      >
                        <option value="square">Square (32 x 32)</option>
                        <option value="rectangle">Wide (up to 150 x 32)</option>
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Square shows the logo in a 32px circle. Wide keeps a long
                        logo readable: 32px tall and up to 150px wide &mdash; not
                        the full width of the header.
                      </p>
                      {(logoLightNeedsRecrop || logoDarkNeedsRecrop) && (
                        <div className="mt-2 p-2.5 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30">
                          <p className="text-xs text-amber-800 dark:text-amber-200">
                            Your saved logo is still cropped for the old shape, so
                            it will look the same until you re-crop it. Use the
                            <strong> Re-crop for this shape</strong> button under
                            each logo above.
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3">
                      <label className="flex items-center space-x-2 font-medium text-gray-900 dark:text-white">
                        <input
                          type="checkbox"
                          checked={showHeaderTitles}
                          onChange={(e) => setShowHeaderTitles(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>Show Header Text</span>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Turn this off to show only your logo in the widget header.
                      </p>
                      <div
                        className={`pl-3 border-l-2 border-gray-200 dark:border-gray-700 transition-opacity ${
                          showHeaderTitles ? 'opacity-100' : 'opacity-40'
                        }`}
                      >
                        <label
                          className={`flex items-center space-x-2 ${
                            showHeaderTitles ? 'cursor-pointer' : 'cursor-not-allowed'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={showHeaderTitles && showAgentName}
                            disabled={!showHeaderTitles}
                            onChange={(e) => setShowAgentName(e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Show agent name
                          </span>
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Your company name always shows while header text is on.
                          Untick this to show the company name on its own.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3">
                      <label className="flex items-center space-x-2 font-medium text-gray-900 dark:text-white">
                        <input
                          type="checkbox"
                          checked={showAiIcon}
                          onChange={(e) => setShowAiIcon(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600"
                        />
                        <span>Show AI Response Icon</span>
                      </label>
                      {showAiIcon && (
                        <>
                          <div className="grid grid-cols-2 gap-2 mb-3">
                            <ImageUploadWithCrop
                              label="Upload Light Icon"
                              initialImage={aiIconLight}
                              onImageChange={(blob, preview) => {
                                setAiIconLightBlob(blob);
                                setAiIconLight(preview || '');
                              }}
                            />
                            <ImageUploadWithCrop
                              label="Upload Dark Icon"
                              initialImage={aiIconDark}
                              onImageChange={(blob, preview) => {
                                setAiIconDarkBlob(blob);
                                setAiIconDark(preview || '');
                              }}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Icon Position</label>
                            <select value={aiIconPosition} onChange={(e) => setAiIconPosition(e.target.value as 'left' | 'right')} className="input-field text-sm p-2">
                              <option value="left">Left</option>
                              <option value="right">Right</option>
                            </select>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3">
                      <label className="flex items-center space-x-2 font-medium text-gray-900 dark:text-white">
                        <input
                          type="checkbox"
                          checked={showAiLabel}
                          onChange={(e) => setShowAiLabel(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600"
                        />
                        <span>Show AI Text Label</span>
                      </label>
                      {showAiLabel && (
                        <>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Label Text</label>
                            <input type="text" value={aiLabelText} onChange={(e) => setAiLabelText(e.target.value)} placeholder="e.g. AI" className="input-field text-sm p-2" />
                            <p className="text-xs text-gray-400 mt-1">Enabling this with "AI" in the text also satisfies the disclosure requirement.</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3">
                      <label className="flex items-center space-x-2 font-medium text-gray-900 dark:text-white">
                        <input
                          type="checkbox"
                          checked={showCustomerIcon}
                          onChange={(e) => setShowCustomerIcon(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600"
                        />
                        <span>Show Customer Icon</span>
                      </label>
                      {showCustomerIcon && (
                        <>
                          <ImageUploadWithCrop
                            label="Upload Circular Icon"
                            initialImage={customerIconUrl}
                            onImageChange={(blob, preview) => {
                              setCustomerIconBlob(blob);
                              setCustomerIconUrl(preview || '');
                            }}
                          />
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Icon Position</label>
                            <select value={customerIconPosition} onChange={(e) => setCustomerIconPosition(e.target.value as 'left' | 'right')} className="input-field text-sm p-2">
                              <option value="left">Left</option>
                              <option value="right">Right</option>
                            </select>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3">
                      <label className="flex items-center space-x-2 font-medium text-gray-900 dark:text-white">
                        <input
                          type="checkbox"
                          checked={showCustomerLabel}
                          onChange={(e) => setShowCustomerLabel(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600"
                        />
                        <span>Show Customer Text Label</span>
                      </label>
                      {showCustomerLabel && (
                        <>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Label Text</label>
                            <input type="text" value={customerLabelText} onChange={(e) => setCustomerLabelText(e.target.value)} placeholder="e.g. You" className="input-field text-sm p-2" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Response Font Color</label>
                            <div className="flex items-center space-x-2">
                              <input type="color" value={customerFontColor} onChange={(e) => setCustomerFontColor(e.target.value)} className="w-8 h-8 rounded border border-gray-300 cursor-pointer" />
                              <input type="text" value={customerFontColor} onChange={(e) => setCustomerFontColor(e.target.value)} className="input-field text-sm p-2 flex-1" />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Sparkles className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-900 dark:text-blue-100">
                        Features Enabled
                      </h4>
                      <ul className="text-sm text-blue-700 dark:text-blue-300 mt-2 space-y-1">
                        <li>✓ Voice input/output support</li>
                        <li>✓ Automatic ticket creation on customer escalations</li>
                        <li>✓ Google Search URL for storage as Article</li>
                        <li>✓ Upload files as Articles</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Mail className="w-5 h-5 text-primary mt-0.5" />
                    <div className="w-full">
                      <h4 className="font-medium text-blue-900 dark:text-blue-100">
                        Email Channel Ready
                      </h4>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                        Your agent can be reached via email at:
                      </p>
                      <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded border border-blue-200 dark:border-blue-800 font-mono text-xs text-gray-800 dark:text-gray-200 break-all select-all">
                        {agentEmail
                          ? agentEmail
                          : agentId
                            ? `${agentId}.${companyName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() || 'company'}@${AGENT_EMAIL_DOMAIN}`
                            : `[agent-id].${companyName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() || 'company'}@${AGENT_EMAIL_DOMAIN}`
                        }
                      </div>
                      {!agentEmail && (
                        <p className="text-xs text-blue-500 dark:text-blue-400 mt-1 italic">
                          Preview — final email is saved on creation.
                        </p>
                      )}

                      {/* Auto-respond toggle */}
                      <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                        <label className="flex items-center justify-between gap-3 cursor-pointer">
                          <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Auto-respond to incoming emails</span>
                          <button
                            type="button"
                            onClick={() => setAutoRespondEmails(!autoRespondEmails)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${autoRespondEmails ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                              }`}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${autoRespondEmails ? 'translate-x-4' : 'translate-x-0.5'
                              }`} />
                          </button>
                        </label>
                        {autoRespondEmails ? (
                          <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                            <p className="text-xs text-amber-700 dark:text-amber-300">
                              ⚠️ <strong>Before enabling auto-respond:</strong> We strongly recommend intensive testing using the <strong>Generate AI Reply</strong> feature in <strong>Inbox → Emails</strong> to validate your agent's responses. <br /><br />
                              <strong>Note:</strong> Auto-replies will <em>only</em> trigger if this Agent is specifically addressed in the "To" line (not CC/BCC) and if this Agent is Published (Live).
                            </p>
                          </div>
                        ) : (
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            ℹ️ You can manually generate AI-drafted replies in <strong>Inbox → Emails</strong> anytime.
                          </p>
                        )}
                      </div>

                      {/* Voice selector */}
                      <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                        <div>
                          <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Voice</span>
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">Select the voice your agent speaks with.</p>
                        </div>
                        <div className="mt-2 flex items-center gap-2 min-w-0">
                        <select
                          value={voiceName}
                          onChange={(e) => setVoiceName(e.target.value)}
                          className="flex-1 min-w-0 rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          {/*
                            Female voices only — picker matches the
                            single deployed HeyGen avatar. Pre-existing
                            male-voice agents auto-migrate to Aoede on
                            next save via Storm's server-side backfill.
                          */}
                          {[
                            { name: 'Aoede', style: 'Breezy' },
                            { name: 'Kore', style: 'Firm' },
                            { name: 'Leda', style: 'Youthful' },
                            { name: 'Zephyr', style: 'Bright' },
                            { name: 'Callirrhoe', style: 'Easy-going' },
                            { name: 'Autonoe', style: 'Bright' },
                            { name: 'Despina', style: 'Smooth' },
                            { name: 'Erinome', style: 'Clear' },
                            { name: 'Laomedeia', style: 'Upbeat' },
                            { name: 'Achernar', style: 'Soft' },
                            { name: 'Gacrux', style: 'Mature' },
                            { name: 'Pulcherrima', style: 'Forward' },
                            { name: 'Vindemiatrix', style: 'Gentle' },
                            { name: 'Sulafat', style: 'Warm' },
                          ].map((v) => (
                            <option key={v.name} value={v.name}>{v.name} — {v.style}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={handleVoicePreview}
                          disabled={isPreviewLoading}
                          title="Preview voice"
                          className="p-2 rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                        >
                          {isPreviewLoading
                            ? <span className="inline-block w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                            : <Volume2 className="w-4 h-4" />}
                        </button>
                        </div>
                        {previewError && <p className="text-xs text-red-500 mt-1">{previewError}</p>}

                        {/* LiveAvatar toggle — sub-option of Voice */}
                        <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                          <label
                            className={`flex items-center justify-between gap-3 ${canUseAvatar ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}
                            title={canUseAvatar ? '' : avatarUnavailableCopy}
                          >
                            <div className="min-w-0">
                              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Enable AI Avatar for this agent</span>
                              <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                                {canUseAvatar
                                  ? 'Render a realistic avatar during voice conversations. Falls back to plain voice if disabled.'
                                  : `${avatarUnavailableCopy}.`}
                              </p>
                              {/* Learn more — renders only when the article URL
                                  env is configured (no broken link when unset). */}
                              {APP_CONFIG.liveAvatarArticleUrl && (
                                <a
                                  href={APP_CONFIG.liveAvatarArticleUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-primary hover:underline"
                                >
                                  Learn more
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                            <button
                              type="button"
                              disabled={!canUseAvatar}
                              onClick={() => canUseAvatar && setAvatarEnabled(!avatarEnabled)}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${avatarEnabled && canUseAvatar ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'} ${!canUseAvatar ? 'cursor-not-allowed' : ''}`}
                            >
                              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${avatarEnabled && canUseAvatar ? 'translate-x-4' : 'translate-x-0.5'}`} />
                            </button>
                          </label>

                          {/* Plan-LiveAvatarInstructionalPage F2 — additive note
                              explaining how AI Avatar access works (distribution-
                              aware) + that usage is billed. Does NOT change the
                              toggle/disabled behavior above. */}
                          <div className="mt-2 p-2 bg-blue-100/60 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
                            <p className="text-xs text-blue-800 dark:text-blue-200 flex items-start gap-1.5">
                              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                              <span>
                                {IS_SHOPIFY_BUILD
                                  ? 'AI Avatar is included in the Business plan.'
                                  : 'AI Avatar requires the AI Avatar add-on.'}{' '}
                                Usage is tracked on your{' '}
                                <a
                                  href="/billing"
                                  onClick={(e) => e.stopPropagation()}
                                  className="font-semibold text-primary hover:underline"
                                >
                                  Billing page
                                </a>
                                .
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* CSAT toggle */}
                      <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                        <label className="flex items-center justify-between gap-3 cursor-pointer">
                          <div>
                            <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Ask for CSAT rating</span>
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">Agent will politely ask for a 1–5 satisfaction score at the end of resolved conversations.</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setAskForCsat(!askForCsat)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${askForCsat ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                              }`}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${askForCsat ? 'translate-x-4' : 'translate-x-0.5'
                              }`} />
                          </button>
                        </label>
                      </div>

                      {/* Chat History toggle */}
                      <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                        <label className="flex items-center justify-between gap-3 cursor-pointer">
                          <div>
                            <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Chat History</span>
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">Save and display previous conversation history in the widget</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setEnableChatHistory(!enableChatHistory)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${enableChatHistory ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                              }`}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${enableChatHistory ? 'translate-x-4' : 'translate-x-0.5'
                              }`} />
                          </button>
                        </label>
                      </div>

                      {/* Inactivity Follow-up toggle */}
                      <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                        <label className="flex items-center justify-between gap-3 cursor-pointer">
                          <div>
                            <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Inactivity follow-up</span>
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">Send a follow-up if the customer goes quiet after your agent's last reply.</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFollowUpEnabled(!followUpEnabled)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${followUpEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                              }`}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${followUpEnabled ? 'translate-x-4' : 'translate-x-0.5'
                              }`} />
                          </button>
                        </label>
                        <div
                          className="overflow-hidden transition-all duration-300 ease-in-out"
                          style={{ maxHeight: followUpEnabled ? '160px' : '0px', opacity: followUpEnabled ? 1 : 0 }}
                        >
                          <div className="mt-3 flex items-center gap-2">
                            <span className="text-xs text-blue-700 dark:text-blue-300">Follow-up after</span>
                            <input
                              type="number"
                              min={1}
                              max={10}
                              step={1}
                              value={followUpIntervalMinutes}
                              onChange={(e) => setFollowUpIntervalMinutes(clampInterval(Number(e.target.value)))}
                              className="w-16 rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-blue-900 dark:text-blue-100 text-center"
                            />
                            <span className="text-xs text-blue-700 dark:text-blue-300">minutes</span>
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-xs text-blue-700 dark:text-blue-300">Max follow-ups</span>
                            <input
                              type="number"
                              min={1}
                              max={3}
                              step={1}
                              value={followUpMaxCount}
                              onChange={(e) => setFollowUpMaxCount(Math.min(3, Math.max(1, Math.round(Number(e.target.value)))))}
                              className="w-16 rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-blue-900 dark:text-blue-100 text-center"
                            />
                            <span className="text-xs text-blue-700 dark:text-blue-300">per session</span>
                          </div>
                          <p className="text-[11px] text-blue-500 dark:text-blue-400 mt-1.5">
                            ℹ️ Up to {followUpMaxCount} follow-up{followUpMaxCount !== 1 ? 's' : ''}, every {followUpIntervalMinutes} minute{followUpIntervalMinutes !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>

                      {/* Talk-to-Human toggle + availability schedule
                          (Plan-TalkToHumanToggleSchedule.md). Mirrors the
                          Inactivity Follow-up pill-toggle → revealed sub-form
                          pattern above. Back-compat: default ON.
                          THREE toggles, deliberately separated: the FEATURE
                          (`human_handoff_enabled`) and its two independent
                          PRESENTATION gates — `human_handoff_button_visible`
                          (in-hours button) and `human_handoff_hours_visible`
                          (off-hours notice). They cover opposite halves of the
                          day and neither suppresses the other. Clients kept
                          conflating the feature with its presentation — killing
                          handoff entirely when they only wanted the button gone
                          — hence the callout. */}
                      <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                        <label className="flex items-center justify-between gap-3 cursor-pointer">
                          <div className="flex items-start gap-2">
                            <Headphones className="w-4 h-4 mt-0.5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                            <div>
                              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Enable human handoff</span>
                              <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">The whole feature: your team can take over chats, and the AI can bring in a person when it judges it necessary. Turn this off and there are no live agents at all.</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setHandoffEnabled(!handoffEnabled)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${handoffEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                              }`}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${handoffEnabled ? 'translate-x-4' : 'translate-x-0.5'
                              }`} />
                          </button>
                        </label>
                        <div
                          className="overflow-hidden transition-all duration-300 ease-in-out"
                          style={{ maxHeight: handoffEnabled ? '1700px' : '0px', opacity: handoffEnabled ? 1 : 0 }}
                        >
                          {/* Presentation-only gate on the customer CTA. Lives
                              INSIDE the enabled-reveal because it is meaningless
                              when the feature is off (no button either way). */}
                          <label className="mt-3 flex items-center justify-between gap-3 cursor-pointer">
                            <div className="flex items-start gap-2">
                              <Eye className="w-4 h-4 mt-0.5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                              <div>
                                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Show "Talk to a live agent" button</span>
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">Appearance only, and only during your staffed hours. Turn this off and customers simply won't see the button — human handoff keeps working exactly as it does now, and the off-hours notice below is unaffected.</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setHandoffButtonVisible(!handoffButtonVisible)}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${handoffButtonVisible ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                                }`}
                            >
                              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${handoffButtonVisible ? 'translate-x-4' : 'translate-x-0.5'
                                }`} />
                            </button>
                          </label>

                          {/* Presentation-only gate on the OFF-HOURS notice. Also
                              suppresses the hours in the AI's replies (backend
                              drops them from the prompt and omits the label from
                              the widget payload) — the callout says so, because a
                              client hiding their schedule would be surprised to
                              hear the bot recite it. */}
                          <label className="mt-3 flex items-center justify-between gap-3 cursor-pointer">
                            <div className="flex items-start gap-2">
                              <CalendarClock className="w-4 h-4 mt-0.5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                              <div>
                                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Show "Our team is available…" notice</span>
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">Appearance only. When your team is offline, customers see a note telling them when you're back. Turn this off and they see nothing — your schedule is never shown to them, and the AI won't mention it either.</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setHandoffHoursVisible(!handoffHoursVisible)}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${handoffHoursVisible ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                                }`}
                            >
                              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${handoffHoursVisible ? 'translate-x-4' : 'translate-x-0.5'
                                }`} />
                            </button>
                          </label>

                          {/* ONE emphasized callout covering all three toggles —
                              amber against the blue card so it reads as a warning,
                              not muted helper text. Three separate warnings would
                              be worse than one clear one. */}
                          <div className="mt-3 rounded-lg border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/40 p-3">
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-amber-900 dark:text-amber-100">These three toggles do different things — don't mix them up</p>
                                <ul className="mt-1.5 space-y-1 text-[11px] leading-snug text-amber-800 dark:text-amber-200">
                                  <li>
                                    <strong className="font-semibold">Enable human handoff</strong> is the feature. Off = no live agents at all: your team gets nothing, and the AI can never escalate to a person.
                                  </li>
                                  <li>
                                    <strong className="font-semibold">Show "Talk to a live agent" button</strong> is only what customers see <em>during</em> your staffed hours. Off = the button is hidden, but handoff still works: your team still takes over on your schedule, and the AI can still bring in a person when it judges it necessary.
                                  </li>
                                  <li>
                                    <strong className="font-semibold">Show "Our team is available…" notice</strong> is only what customers see <em>outside</em> your staffed hours. Off = they see nothing instead of your hours. This hides your schedule from the AI's replies too, not just the widget — the bot will never say when you're back.
                                  </li>
                                </ul>
                                <p className="mt-1.5 text-[11px] leading-snug text-amber-700 dark:text-amber-300">
                                  The two appearance toggles are independent of each other: hiding the button does <strong className="font-semibold">not</strong> hide the off-hours notice, and hiding the notice does not hide the button. They cover opposite halves of the day. Both are also independent of the hours below — you can hide either one and still run staffed hours.
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Availability mode: 24/7 vs Set hours */}
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <span className="text-xs text-blue-700 dark:text-blue-300">Availability</span>
                            <div className="inline-flex rounded-lg border border-blue-200 dark:border-blue-700 overflow-hidden">
                              <button
                                type="button"
                                onClick={() => setHandoffUseSchedule(false)}
                                className={`px-2.5 py-1 text-xs font-medium transition-colors ${!handoffUseSchedule
                                  ? 'bg-primary text-white'
                                  : 'bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300'
                                  }`}
                              >
                                Available 24/7
                              </button>
                              <button
                                type="button"
                                onClick={() => setHandoffUseSchedule(true)}
                                className={`px-2.5 py-1 text-xs font-medium transition-colors ${handoffUseSchedule
                                  ? 'bg-primary text-white'
                                  : 'bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300'
                                  }`}
                              >
                                Set hours
                              </button>
                            </div>
                          </div>

                          {/* Schedule editor — timezone + per-weekday range */}
                          <div
                            className="overflow-hidden transition-all duration-300 ease-in-out"
                            style={{ maxHeight: handoffUseSchedule ? '820px' : '0px', opacity: handoffUseSchedule ? 1 : 0 }}
                          >
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              <span className="text-xs text-blue-700 dark:text-blue-300">Time zone</span>
                              <select
                                value={handoffTimezone}
                                onChange={(e) => setHandoffTimezone(e.target.value)}
                                className="rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-blue-900 dark:text-blue-100"
                              >
                                {HANDOFF_TIMEZONES.map((tz) => (
                                  <option key={tz} value={tz}>{tz}</option>
                                ))}
                              </select>
                            </div>

                            <div className="mt-3 space-y-2">
                              {HANDOFF_WEEKDAYS.map(({ key, label }) => {
                                const day = handoffDays[key];
                                return (
                                  <div key={key} className="flex flex-wrap items-center gap-2">
                                    <button
                                      type="button"
                                      aria-label={`${label} availability`}
                                      onClick={() => setHandoffDays((prev) => ({
                                        ...prev,
                                        [key]: { ...prev[key], enabled: !prev[key].enabled },
                                      }))}
                                      className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors flex-shrink-0 ${day.enabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                                        }`}
                                    >
                                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${day.enabled ? 'translate-x-4' : 'translate-x-0.5'
                                        }`} />
                                    </button>
                                    <span className="w-8 text-xs font-medium text-blue-900 dark:text-blue-100">{label}</span>
                                    {day.enabled ? (
                                      <div className="flex items-center gap-1.5">
                                        <input
                                          type="time"
                                          value={day.start}
                                          onChange={(e) => setHandoffDays((prev) => ({
                                            ...prev,
                                            [key]: { ...prev[key], start: e.target.value },
                                          }))}
                                          className="rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-800 px-1.5 py-1 text-xs text-blue-900 dark:text-blue-100"
                                        />
                                        <span className="text-xs text-blue-500 dark:text-blue-400">–</span>
                                        <input
                                          type="time"
                                          value={day.end}
                                          onChange={(e) => setHandoffDays((prev) => ({
                                            ...prev,
                                            [key]: { ...prev[key], end: e.target.value },
                                          }))}
                                          className="rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-800 px-1.5 py-1 text-xs text-blue-900 dark:text-blue-100"
                                        />
                                      </div>
                                    ) : (
                                      <span className="text-xs text-blue-400 dark:text-blue-500 italic">Closed</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <p className="text-[11px] text-blue-500 dark:text-blue-400 mt-2">
                              ℹ️ Outside these hours, customers see a note telling them when you're back, instead of the live button — unless you've hidden the button or the notice above, in which case they see nothing. Your team's handoffs are unaffected either way. An end time earlier than the start is treated as crossing midnight.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Stylist (Shopify build only) — sits below the Features
                    Enabled / Email Channel cards. Persists `stylist_enabled`
                    on the agent config. The Stylist engine is a future build;
                    this is the per-agent activation surface only. */}
                {IS_SHOPIFY_BUILD && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Wand2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <div className="w-full min-w-0">
                        <h4 className="font-medium text-blue-900 dark:text-blue-100">
                          AI Stylist
                        </h4>

                        {/* Toggle */}
                        <div className="mt-2">
                          <label className="flex items-center justify-between gap-3 cursor-pointer">
                            <div className="min-w-0">
                              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Enable AI Stylist for this agent</span>
                              <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                                Lets shoppers get personalized, image-based product recommendations ('looks') from your store's catalog.
                              </p>
                              {/* Learn more — renders only when the article URL
                                  env is configured (no broken link when unset). */}
                              {APP_CONFIG.stylistArticleUrl && (
                                <a
                                  href={APP_CONFIG.stylistArticleUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-primary hover:underline"
                                >
                                  Learn more
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setStylistEnabled(!stylistEnabled)}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${stylistEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
                            >
                              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${stylistEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                            </button>
                          </label>
                        </div>

                        {/* Sync-first disclaimer — soft note (does NOT disable
                            the toggle). The Stylist builds looks from the synced
                            catalog mirror, so merchants should sync once on the
                            Stylist page before enabling here. */}
                        <div className="mt-3 p-2 bg-blue-100/60 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
                          <p className="text-xs text-blue-800 dark:text-blue-200 flex items-start gap-1.5">
                            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                            <span>
                              Sync your product catalog first (
                              <a
                                href="/stylist"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="font-semibold text-primary hover:underline"
                              >
                                AI Stylist page → Sync catalog
                              </a>
                              ) so the Stylist can build looks from your live products.
                            </span>
                          </p>
                        </div>

                        {/* Credit-cost disclaimer — visible while the Stylist
                            section shows (amber warning box, matches the
                            auto-respond warning styling).
                            🚨 TWIN — the same claim is made on the Stylist page
                            (`pages/Stylist.tsx`, "What it costs"); THE TWO MUST
                            MOVE TOGETHER.
                            🚨 Legacy-only: the +2 debit rides `chat_conversations`,
                            retired as a billing unit under pooled billing. Kept
                            as a cost warning in both modes, but the pooled
                            wording names no unit and no number — the Stylist is
                            genuinely more expensive than a chat either way, and
                            that is the part a merchant needs before enabling it. */}
                        <div className="mt-3 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                          <p className="text-xs text-amber-700 dark:text-amber-300">
                            {showLegacyCredits ? (
                              <>
                                ⚠️ <strong>Heads up:</strong> each Stylist result uses <strong>2 chat-conversation credits</strong>. The chat that starts the Stylist is counted separately.
                              </>
                            ) : (
                              <>
                                ⚠️ <strong>Heads up:</strong> each Stylist result runs <strong>two AI calls</strong> and uses noticeably more of your plan usage than a normal chat. The chat that starts the Stylist is counted separately.
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Virtual Try-On (Shopify build only) — sits below the AI
                    Stylist card. Persists `virtual_tryon_enabled` on the agent
                    config. The try-on pipeline is a later phase; this is the
                    per-agent activation surface only. Gated on the master
                    launch flag (virtualTryonEnabled) so the surface stays
                    hidden per deployment until Virtual Try-On is launched. */}
                {IS_SHOPIFY_BUILD && APP_CONFIG.virtualTryonEnabled && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Shirt className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <div className="w-full min-w-0">
                        <h4 className="font-medium text-purple-900 dark:text-purple-100">
                          Virtual Try-On
                        </h4>

                        {/* Toggle */}
                        <div className="mt-2">
                          <label className="flex items-center justify-between gap-3 cursor-pointer">
                            <div className="min-w-0">
                              <span className="text-sm font-medium text-purple-900 dark:text-purple-100">Enable Virtual Try-On for this agent</span>
                              <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
                                Lets shoppers see a product (or a Stylist look) on a photo of themselves before they buy.
                              </p>
                              {/* Learn more — renders only when the article URL
                                  env is configured (no broken link when unset). */}
                              {APP_CONFIG.virtualTryonArticleUrl && (
                                <a
                                  href={APP_CONFIG.virtualTryonArticleUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-primary hover:underline"
                                >
                                  Learn more
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setVirtualTryonEnabled(!virtualTryonEnabled)}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${virtualTryonEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
                            >
                              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${virtualTryonEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                            </button>
                          </label>
                        </div>

                        {/* Sync-first disclaimer — soft note (does NOT disable
                            the toggle). Try-On resolves garments from the synced
                            catalog mirror, so merchants should sync once on the
                            Stylist page before enabling here. */}
                        <div className="mt-3 p-2 bg-purple-100/60 dark:bg-purple-900/30 rounded-lg border border-purple-200 dark:border-purple-800">
                          <p className="text-xs text-purple-800 dark:text-purple-200 flex items-start gap-1.5">
                            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                            <span>
                              Sync your product catalog first (
                              <a
                                href="/stylist"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="font-semibold text-primary hover:underline"
                              >
                                AI Stylist page → Sync catalog
                              </a>
                              ) so Try-On can pull garment images from your live products.
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Public Help Center URL. Edit-mode only — the server mints the
                    slug at creation, so there is nothing to edit for a new agent.
                    Deliberately FROZEN: seeded from the saved value and never
                    re-derived from the agent name. Renaming must not move the
                    public URL, which is exactly the bug this fixes. */}
                {isEdit && (
                  <div className="space-y-4 pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Help Center</h3>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Public help center URL
                      </label>
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          type="text"
                          value={hcSlug}
                          onChange={(e) => {
                            // Permissive while typing — a trailing hyphen must
                            // survive, or "spago-shopper" can't be typed at all.
                            setHcSlug(slugifyTyping(e.target.value));
                            setHcSlugError(null);
                          }}
                          onBlur={() => {
                            // Strict on leave: "spago-" settles to "spago".
                            // If they cleared it, snap back to the saved slug —
                            // an empty box would imply the URL is gone when it
                            // isn't (we don't send empty).
                            setHcSlug(slugifyStrict(hcSlug) || (helpCenterSlug ?? ''));
                          }}
                          placeholder="your-help-center"
                          aria-invalid={!!hcSlugError}
                          className={`flex-1 min-w-0 px-3 py-2 text-sm font-mono rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white border focus:outline-none focus:ring-2 ${hcSlugError
                            ? 'border-red-400 dark:border-red-500 focus:ring-red-500'
                            : 'border-gray-200 dark:border-gray-700 focus:ring-indigo-500'
                            }`}
                        />
                      </div>

                      {/* Live full-URL preview — the STRICT form, i.e. what will
                          actually be saved. Showing the raw typing value would
                          promise a URL ("…/spago-") that never gets sent. */}
                      {hcWorkspaceSlug && hcSlugStrict ? (
                        <p className="mt-1.5 text-xs font-mono text-gray-500 dark:text-gray-400 break-all">
                          {hcBaseHost}/help/{hcWorkspaceSlug}/<span className="font-semibold text-gray-800 dark:text-gray-200">{hcSlugStrict}</span>
                        </p>
                      ) : (
                        <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                          {hcSlugStrict ? 'Workspace URL unavailable.' : 'Enter a URL for your help center.'}
                        </p>
                      )}

                      {/* Server-side rejection (duplicate / bad format / reserved
                          word) pinned to the field — not a toast that vanishes. */}
                      {hcSlugError && (
                        <p className="mt-1.5 flex items-start gap-1.5 text-xs text-red-600 dark:text-red-400">
                          <AlertTriangle className="w-3.5 h-3.5 mt-px flex-shrink-0" />
                          <span>{hcSlugError}</span>
                        </p>
                      )}
                    </div>

                    {/* Emphasized warning — this is a destructive-by-default edit. */}
                    <div className="rounded-lg border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/40 p-3">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-amber-900 dark:text-amber-100">
                            Changing this URL breaks every existing link to your help center
                          </p>
                          <p className="mt-1 text-[11px] leading-snug text-amber-800 dark:text-amber-200">
                            Bookmarks, links you've shared, and search-engine results will all stop working and show a "not found" page. Only change this if you really mean to.
                          </p>
                          <p className="mt-1.5 text-[11px] leading-snug text-amber-700 dark:text-amber-300">
                            <strong className="font-semibold">Renaming your agent will NOT change this URL.</strong> It stays exactly as it is until you change it here, on purpose.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Form Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <button
                onClick={handleClose}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                {isEdit ? 'Cancel' : 'Skip for now'}
              </button>

              <div className="flex space-x-3">
                <button
                  onClick={handleSaveAgent}
                  className="btn-primary"
                  disabled={loading || !agentName || !companyName || !welcomeMessage}
                >
                  {loading ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Agent')}
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: Preview */}
          <div className="hidden lg:flex lg:w-[400px] bg-gray-50 dark:bg-gray-950 flex-col border-l border-gray-100 dark:border-gray-700">
            <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Live Preview</span>
              <div className="flex space-x-1">
                <div className="w-2 h-2 rounded-full bg-red-400"></div>
                <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                <div className="w-2 h-2 rounded-full bg-green-400"></div>
              </div>
            </div>
            <div className="flex-1 p-6 flex items-center justify-center">
              <WidgetPreview
                agentName={agentName}
                companyName={companyName}
                logoUrlLight={logoLight}
                logoUrlDark={logoDark}
                brandColorLight={brandColor}
                brandColorDark={brandColorDark}
                welcomeMessage={welcomeMessage}
                launcherType={launcherType}
                launcherIconId={launcherIconId}
                launcherIconUrlLight={launcherIconLight}
                launcherIconUrlDark={launcherIconDark}
                showAiIcon={showAiIcon}
                aiIconPosition={aiIconPosition}
                showAiLabel={showAiLabel}
                aiLabelText={aiLabelText}
                showCustomerIcon={showCustomerIcon}
                customerIconPosition={customerIconPosition}
                showCustomerLabel={showCustomerLabel}
                customerLabelText={customerLabelText}
                customerFontColor={customerFontColor}
                headerLogoShape={headerLogoShape}
                showHeaderTitles={showHeaderTitles}
                showAgentName={showAgentName}
                aiIconUrlLight={aiIconLight}
                aiIconUrlDark={aiIconDark}
                customerIconUrl={customerIconUrl}
                avatarEnabled={avatarEnabled && canUseAvatar}
                showChatHistory={enableChatHistory}
                voiceName={voiceName}
                handoffEnabled={handoffEnabled}
                handoffButtonVisible={handoffButtonVisible}
                handoffHoursVisible={handoffHoursVisible}
                handoffSchedule={handoffPreviewSchedule}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentEditorModal;